import { useState, useMemo } from 'react'
import { ExternalLinkIcon, Smartphone, Monitor } from 'lucide-react'
import {
  MobilePreview,
  ViewportPreset,
  VIEWPORT_PRESETS,
} from 'components/MobilePreview'
import { Modal } from 'components/UI/Modal/Modal'
import FbButton from '../../../components/FbUI/FbButton'
import { OfferPage } from '../../../components/offer-page/offer-page'
import { RebateOffer } from '../tradespend_api'
import apiService from '../../../services/api'
import { API_HOST } from 'utils/http-client'

function PreviewModal({
  url,
  offerName,
  offer,
  isOpen,
  onClose,
}: {
  url: string
  offerName: string
  offer?: RebateOffer
  isOpen: boolean
  onClose: () => void
}) {
  const [isMobileView, setIsMobileView] = useState(true)
  const [selectedViewport, setSelectedViewport] =
    useState<ViewportPreset>('iPhone')
  const api = apiService()
  const { data: logos } = api.useGetCompanyLogos()

  const primaryLogo = useMemo(() => {
    return logos?.find((logo) => logo.is_primary) || logos?.[0]
  }, [logos])

  const previewData = useMemo(() => {
    if (!offer) return null

    // Use manufacturer_details.name from the API response (actual company name)
    // Fall back to terms.company_name for backwards compatibility
    const manufacturerName =
      offer.manufacturer_details?.name || offer.terms.company_name || ''

    // Use manufacturer_details.logo from API, fall back to fetched logo
    const manufacturerLogo =
      offer.manufacturer_details?.logo ||
      (primaryLogo ? new URL(primaryLogo.logo_url ?? '', API_HOST).href : '')

    return {
      firstbite_id: '',
      offer: {
        name: offer.name,
        code: offer.code,
        type: offer.offer_type,
        branding: offer.branding || 'COMPANY',
        description: offer.description,
        learn_more_url: offer.learn_more_url,
        valid_from: offer.valid_from,
        valid_to: offer.valid_to,
        terms: offer.terms,
        manufacturer_details: {
          name: manufacturerName,
          logo: manufacturerLogo,
          rebate_support_email:
            offer.manufacturer_details?.rebate_support_email,
        },
      },
    }
  }, [offer, primaryLogo])

  const isExpired = offer?.state === 'EXPIRED'
  const isPaused = offer?.state === 'PAUSED'

  return (
    <Modal
      open={isOpen}
      hideCancelButton
      onOpenChange={(open: boolean) => !open && onClose()}
      title={`Preview: ${offerName}`}
      size="2xl"
      headerExtra={
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">Preview Size: </span>
          <select
            value={selectedViewport}
            onChange={(e) =>
              setSelectedViewport(e.target.value as ViewportPreset)
            }
            className="text-sm py-1 w-30 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {Object.entries(VIEWPORT_PRESETS).map(([key, value]) => (
              <option key={key} value={key}>
                {value.label}
              </option>
            ))}
          </select>
        </div>
      }
    >
      <div className="flex flex-col h-[650px]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <FbButton
              variant={isMobileView ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setIsMobileView(true)}
            >
              <Smartphone className="h-4 w-4 mr-1" />
              Mobile
            </FbButton>
            <FbButton
              variant={!isMobileView ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setIsMobileView(false)}
            >
              <Monitor className="h-4 w-4 mr-1" />
              Desktop
            </FbButton>
          </div>

          <FbButton
            variant="secondary"
            size="sm"
            onClick={() => window.open(url, '_blank')}
          >
            <ExternalLinkIcon className="h-4 w-4 mr-1" />
            Open in new tab
          </FbButton>
        </div>

        <div className="flex-1 rounded-lg overflow-hidden">
          {isMobileView ? (
            <div className="w-full h-full p-1">
              {previewData ? (
                <MobilePreview selectedViewport={selectedViewport}>
                  <OfferPage
                    isPreview={true}
                    isExpired={isExpired}
                    isPaused={isPaused}
                    data={previewData as any}
                    onClaimOffer={() => {}}
                  />
                </MobilePreview>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">Loading preview...</p>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full h-full p-1">
              {previewData ? (
                <div className="w-full h-full overflow-auto bg-[#E8ECE7]">
                  <OfferPage
                    isPreview={true}
                    isExpired={isExpired}
                    isPaused={isPaused}
                    data={previewData as any}
                    onClaimOffer={() => {}}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">Loading preview...</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default PreviewModal
